import { DragEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import colors from 'tailwindcss/colors'
import { IoImage, IoTrash } from 'react-icons/io5'

import { Button } from '@/components/Button'
import { useModal } from '@/hooks/useModal'
import { useToast } from '@/hooks/useToast'
import { FileWithType, useUpload } from '@/hooks/useUpload'
import {
  createEvent,
  extractApiErrorMessage,
  updateEvent,
} from '@/services/events'
import { uploadEventAssets } from '@/services/upload-event-assets'
import {
  EventMutationPayloadProps,
  EventSummaryProps,
  EventTheme,
} from '@/types/eventsProps'

interface FormEventProps {
  event?: EventSummaryProps
  onSaved: () => Promise<void> | void
}

interface EventFormValues {
  title: string
  editionYear: string
  theme: EventTheme
  shortDescription: string
  description: string
  city: string
  state: string
  startDate: string
  endDate: string
  coverImageUrl: string
  bannerImageUrl: string
  officialPlaylistId: string
  officialUrl: string
  ticketPageUrl: string
  shareTitle: string
  shareDescription: string
  isFeatured: boolean
}

const themeOptions: Array<{ label: string; value: EventTheme }> = [
  { label: 'Sao Joao', value: 'SAO_JOAO' },
  { label: 'Carnaval', value: 'CARNAVAL' },
  { label: 'Reveillon', value: 'REVEILLON' },
  { label: 'Copa do Mundo', value: 'COPA_DO_MUNDO' },
  { label: 'Vaquejada', value: 'VAQUEJADA' },
  { label: 'Festival', value: 'FESTIVAL' },
  { label: 'Other', value: 'OTHER' },
]

function toDateTimeLocal(value?: string | null) {
  if (!value) return ''

  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) {
    return ''
  }

  const timezoneOffset = parsedDate.getTimezoneOffset()
  const localDate = new Date(parsedDate.getTime() - timezoneOffset * 60000)
  return localDate.toISOString().slice(0, 16)
}

function toIsoDateTime(value: string) {
  if (!value) return ''

  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) {
    return ''
  }

  return parsedDate.toISOString()
}

export function FormEvent({ event, onSaved }: FormEventProps) {
  const { closeModal } = useModal()
  const { showToast } = useToast()
  const { formatBytes, formatDate } = useUpload()
  const [isLoading, setIsLoading] = useState(false)
  const [coverFile, setCoverFile] = useState<FileWithType>()
  const [bannerFile, setBannerFile] = useState<FileWithType>()
  const [isDropCover, setIsDropCover] = useState(false)
  const [isDropBanner, setIsDropBanner] = useState(false)

  const { register, handleSubmit, setValue } = useForm<EventFormValues>({
    defaultValues: {
      title: '',
      editionYear: `${new Date().getFullYear()}`,
      theme: 'FESTIVAL',
      shortDescription: '',
      description: '',
      city: '',
      state: '',
      startDate: '',
      endDate: '',
      coverImageUrl: '',
      bannerImageUrl: '',
      officialPlaylistId: '',
      officialUrl: '',
      ticketPageUrl: '',
      shareTitle: '',
      shareDescription: '',
      isFeatured: false,
    },
  })

  const onSubmit = async (data: EventFormValues) => {
    const editionYear = Number(data.editionYear)

    if (!Number.isFinite(editionYear) || editionYear < 2000) {
      showToast('Ano da edicao invalido.', {
        type: 'warning',
        theme: 'light',
      })
      return
    }

    const startDate = toIsoDateTime(data.startDate)
    const endDate = toIsoDateTime(data.endDate)

    if (!startDate || !endDate) {
      showToast('Informe datas de inicio e fim validas.', {
        type: 'warning',
        theme: 'light',
      })
      return
    }

    if (!coverFile && !event?.coverImageUrl) {
      showToast('Adicione a imagem de cover do evento.', {
        type: 'warning',
        theme: 'light',
      })
      return
    }

    if (!bannerFile && !event?.bannerImageUrl) {
      showToast('Adicione a imagem de banner do evento.', {
        type: 'warning',
        theme: 'light',
      })
      return
    }

    const payload: EventMutationPayloadProps = {
      title: data.title,
      editionYear,
      theme: data.theme,
      shortDescription: data.shortDescription,
      description: data.description,
      city: data.city,
      state: data.state,
      startDate,
      endDate,
      coverImageUrl: data.coverImageUrl,
      bannerImageUrl: data.bannerImageUrl,
      officialPlaylistId: data.officialPlaylistId,
      officialUrl: data.officialUrl,
      ticketPageUrl: data.ticketPageUrl,
      shareTitle: data.shareTitle,
      shareDescription: data.shareDescription,
      isFeatured: data.isFeatured,
    }

    try {
      setIsLoading(true)

      const basePayload: EventMutationPayloadProps = payload
      let savedEventId = event?.id
      let savedEventSlug = event?.slug ?? ''

      if (event?.id) {
        const updatedEvent = await updateEvent(event.id, basePayload)
        savedEventSlug = updatedEvent.event.slug
      } else {
        const createdEvent = await createEvent(basePayload)
        savedEventId = createdEvent.event.id
        savedEventSlug = createdEvent.event.slug
      }

      if ((coverFile || bannerFile) && savedEventId) {
        const uploadedFiles = await uploadEventAssets({
          coverFile,
          bannerFile,
          eventSlug: savedEventSlug || data.title,
        })

        const patchPayload = {
          ...basePayload,
          coverImageUrl:
            uploadedFiles.coverFile?.url ?? basePayload.coverImageUrl,
          bannerImageUrl:
            uploadedFiles.bannerFile?.url ?? basePayload.bannerImageUrl,
        }

        await updateEvent(savedEventId, patchPayload)
      }

      if (event?.id) {
        showToast('Evento atualizado com sucesso.', {
          type: 'success',
          theme: 'light',
        })
      } else {
        showToast('Evento criado com sucesso.', {
          type: 'success',
          theme: 'light',
        })
      }

      await Promise.resolve(onSaved())
      closeModal()
    } catch (error) {
      showToast(
        extractApiErrorMessage(error, 'Nao foi possivel salvar o evento.'),
        {
          type: 'error',
          theme: 'light',
        },
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!event) return

    setValue('title', event.title ?? '')
    setValue('editionYear', `${event.editionYear ?? new Date().getFullYear()}`)
    setValue('theme', (event.theme ?? 'FESTIVAL') as EventTheme)
    setValue('shortDescription', event.shortDescription ?? '')
    setValue('description', event.description ?? '')
    setValue('city', event.city ?? '')
    setValue('state', event.state ?? '')
    setValue('startDate', toDateTimeLocal(event.startDate))
    setValue('endDate', toDateTimeLocal(event.endDate))
    setValue('coverImageUrl', event.coverImageUrl ?? '')
    setValue('bannerImageUrl', event.bannerImageUrl ?? '')
    setValue('officialPlaylistId', event.officialPlaylistId ?? '')
    setValue('officialUrl', event.officialUrl ?? '')
    setValue('ticketPageUrl', event.ticketPageUrl ?? '')
    setValue('shareTitle', event.shareTitle ?? '')
    setValue('shareDescription', event.shareDescription ?? '')
    setValue('isFeatured', Boolean(event.isFeatured))
  }, [event, setValue])

  const handleDropImage = (
    e: DragEvent<HTMLDivElement>,
    target: 'cover' | 'banner',
  ) => {
    e.preventDefault()

    const droppedFile = e.dataTransfer.files[0]
    const imageTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp']

    if (!droppedFile || !imageTypes.includes(droppedFile.type)) {
      showToast('Archive type no suported', {
        type: 'error',
        theme: 'light',
      })
      setIsDropCover(false)
      setIsDropBanner(false)
      return
    }

    if (target === 'cover') {
      setCoverFile(droppedFile)
      setIsDropCover(false)
      return
    }

    setBannerFile(droppedFile)
    setIsDropBanner(false)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3">
        <div>
          <h4 className="mb-2 text-sm font-bold">Titulo</h4>
          <input
            {...register('title', { required: true })}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
            placeholder="Sao Joao de Caruaru 2026"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Ano da edicao</h4>
          <input
            {...register('editionYear', { required: true })}
            type="number"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Tema</h4>
          <select
            {...register('theme')}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          >
            {themeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Destaque</h4>
          <label className="mt-2 inline-flex cursor-pointer items-center gap-2 text-sm">
            <input {...register('isFeatured')} type="checkbox" />
            Exibir como destaque
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Cidade</h4>
          <input
            {...register('city', { required: true })}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
            placeholder="Caruaru"
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Estado (UF)</h4>
          <input
            {...register('state', { required: true, maxLength: 2 })}
            maxLength={2}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm uppercase focus:border-gray-500 focus:outline-none"
            placeholder="PE"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Inicio</h4>
          <input
            {...register('startDate', { required: true })}
            type="datetime-local"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Fim</h4>
          <input
            {...register('endDate', { required: true })}
            type="datetime-local"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <h4 className="mb-2 text-sm font-bold">Resumo curto</h4>
        <textarea
          {...register('shortDescription')}
          rows={2}
          className="w-full resize-none rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
        />
      </div>

      <div>
        <h4 className="mb-2 text-sm font-bold">Descricao completa</h4>
        <textarea
          {...register('description')}
          rows={4}
          className="w-full resize-none rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
        <div className="flex flex-col">
          <h4 className="mb-2 text-sm font-bold">Upload cover</h4>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={() => setIsDropCover(true)}
            className="relative flex min-h-[96px] items-center justify-center overflow-hidden rounded-xl border border-dashed border-purple-600 px-4 py-4"
          >
            {coverFile ? (
              <div className="flex w-full min-w-0 items-center gap-3">
                <img
                  src={URL.createObjectURL(coverFile)}
                  alt={coverFile.name}
                  className="h-12 w-16 shrink-0 rounded-lg object-cover"
                />

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {coverFile.name}
                  </p>
                  <span className="text-xs font-normal text-[#71839B]">
                    {formatBytes(coverFile.size)} -{' '}
                    {formatDate(coverFile.lastModified)}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => setCoverFile(undefined)}
                  className="ml-auto shrink-0"
                >
                  <IoTrash color={colors.red[600]} size={20} />
                </button>
              </div>
            ) : event?.coverImageUrl ? (
              <div className="flex w-full items-center justify-center">
                <img
                  src={event.coverImageUrl}
                  alt="event cover"
                  className="h-20 w-full max-w-[220px] rounded-lg object-cover"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 text-center">
                <IoImage color={colors.purple[600]} size={20} />
                <p className="font-semibold text-purple-600">
                  Drop event cover
                </p>
              </div>
            )}

            {isDropCover && (
              <div
                onDrop={(e) => handleDropImage(e, 'cover')}
                onDragLeave={() => setIsDropCover(false)}
                className="absolute top-0 flex h-full w-full items-center justify-center bg-black/80"
              >
                <div className="pointer-events-none">
                  <p className="text-xl font-bold text-white">Drop your file</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col">
          <h4 className="mb-2 text-sm font-bold">Upload banner</h4>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={() => setIsDropBanner(true)}
            className="relative flex min-h-[96px] items-center justify-center overflow-hidden rounded-xl border border-dashed border-purple-600 px-4 py-4"
          >
            {bannerFile ? (
              <div className="flex w-full min-w-0 items-center gap-3">
                <img
                  src={URL.createObjectURL(bannerFile)}
                  alt={bannerFile.name}
                  className="h-12 w-16 shrink-0 rounded-lg object-cover"
                />

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {bannerFile.name}
                  </p>
                  <span className="text-xs font-normal text-[#71839B]">
                    {formatBytes(bannerFile.size)} -{' '}
                    {formatDate(bannerFile.lastModified)}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => setBannerFile(undefined)}
                  className="ml-auto shrink-0"
                >
                  <IoTrash color={colors.red[600]} size={20} />
                </button>
              </div>
            ) : event?.bannerImageUrl ? (
              <div className="flex w-full items-center justify-center">
                <img
                  src={event.bannerImageUrl}
                  alt="event banner"
                  className="h-20 w-full max-w-[220px] rounded-lg object-cover"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 text-center">
                <IoImage color={colors.purple[600]} size={20} />
                <p className="font-semibold text-purple-600">
                  Drop event banner
                </p>
              </div>
            )}

            {isDropBanner && (
              <div
                onDrop={(e) => handleDropImage(e, 'banner')}
                onDragLeave={() => setIsDropBanner(false)}
                className="absolute top-0 flex h-full w-full items-center justify-center bg-black/80"
              >
                <div className="pointer-events-none">
                  <p className="text-xl font-bold text-white">Drop your file</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Playlist oficial (ID)</h4>
          <input
            {...register('officialPlaylistId')}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
            placeholder="playlist_uuid"
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">URL oficial</h4>
          <input
            {...register('officialUrl')}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
            placeholder="https://..."
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">URL da bilheteria</h4>
          <input
            {...register('ticketPageUrl')}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
            placeholder="https://..."
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Titulo de compartilhamento</h4>
          <input
            {...register('shareTitle')}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <h4 className="mb-2 text-sm font-bold">
          Descricao de compartilhamento
        </h4>
        <textarea
          {...register('shareDescription')}
          rows={2}
          className="w-full resize-none rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
        />
      </div>

      <Button
        title={event ? 'Atualizar rascunho' : 'Criar evento'}
        variant="purple"
        isLoading={isLoading}
      />
    </form>
  )
}
